import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  CircleDollarSign,
  Clock,
  Warehouse,
  Shield,
  CheckCircle2,
  ExternalLink,
  Loader2,
  Settings as SettingsIcon,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { api } from "../lib/api";

/**
 * Phase-3 operations panel for the onboarding wizard — Feb 2026.
 *
 * Four defaults sections, each with a one-click "Accept" button that
 * marks it reviewed. The real editing lives in Settings → Integrations
 * (full fields, geofences, keywords, etc.) — a "Customise" link opens
 * that page in a new tab. Keeps the wizard snappy without duplicating
 * the complex editors.
 */

const SECTIONS = [
  {
    key: "pricing",
    anchor: "manual-pricing",
    label: "Manual tow rates",
    sub: "Used by Tow Calc for ad-hoc quotes.",
    icon: CircleDollarSign,
    accent: "bg-emerald-600",
    getter: "/pricing/manual",
    summary: (cfg) => cfg && [
      ["Hourly rate", `$${Number(cfg.hourly_rate).toFixed(2)} / hr`],
      ["After-hours surcharge", `$${Number(cfg.after_hours_surcharge).toFixed(2)}`],
      ["FAF per km", `$${Number(cfg.faf_per_km).toFixed(3)}`],
      ["Minimum charge", `$${Number(cfg.minimum_charge).toFixed(2)}`],
    ],
  },
  {
    key: "oncall",
    anchor: "on-call",
    label: "On-call schedule",
    sub: "When your drivers are on call for after-hours jobs.",
    icon: Clock,
    accent: "bg-amber-600",
    getter: "/oncall/status",
    summary: (cfg) => cfg && [
      [
        "Weekday after-hours",
        `${String(cfg.config?.weekday_start_hour ?? 17).padStart(2, "0")}:${String(cfg.config?.weekday_start_minute ?? 0).padStart(2, "0")} → ` +
          `${String(cfg.config?.weekday_end_hour ?? 8).padStart(2, "0")}:${String(cfg.config?.weekday_end_minute ?? 0).padStart(2, "0")}`,
      ],
      ["Weekends", cfg.config?.weekends_oncall ? "On call all day" : "Not on call"],
      ["Public holidays", cfg.config?.holidays_oncall ? "On call all day" : "Not on call"],
    ],
  },
  {
    key: "storage",
    anchor: "storage-rates",
    label: "Storage rates",
    sub: "Daily storage charges per party (AA, FR, Police, Insurance, Turners).",
    icon: Warehouse,
    accent: "bg-sky-600",
    getter: "/storage-rates",
    summary: (cfg) =>
      cfg?.categories?.slice(0, 4).map((c) => [c.label, `$${Number(c.rate_incl_gst).toFixed(2)} / day`]),
  },
  {
    key: "impound",
    anchor: "police-impound",
    label: "Police impound config",
    sub: "28-day & 6-month impound fees + per-km rules.",
    icon: Shield,
    accent: "bg-rose-700",
    getter: "/integration/police-impound/config",
    summary: (cfg) => cfg && [
      ["Light vehicle threshold", `${cfg.threshold_kg} kg`],
      ["Light 28-day fee", `$${Number(cfg.impound_fee_light_28d).toFixed(2)}`],
      ["Heavy 28-day fee", `$${Number(cfg.impound_fee_heavy_28d).toFixed(2)}`],
      ["Overstay / day (light)", `$${Number(cfg.overstay_per_day_light).toFixed(2)}`],
    ],
  },
];

export default function OnboardingOperationsPanel({ state, onChange }) {
  return (
    <div className="space-y-4" data-testid="onboarding-operations">
      <header>
        <h2 className="text-lg font-bold flex items-center gap-2">Operations &amp; pricing</h2>
        <p className="text-xs text-slate-400 mt-1">
          We've prefilled sensible NZ defaults for each. Glance over, tap
          <strong className="mx-1 text-emerald-400">Accept defaults</strong>
          if they'll do for now, or
          <strong className="mx-1 text-sky-400">Customise</strong>
          to open the full editor in Settings. You can tweak any time later.
        </p>
      </header>
      <div className="space-y-3">
        {SECTIONS.map((s) => (
          <OperationsCard
            key={s.key}
            section={s}
            status={state.operations?.[s.key] || "pending"}
            onChange={onChange}
          />
        ))}
      </div>
    </div>
  );
}

function OperationsCard({ section, status, onChange }) {
  const [cfg, setCfg] = useState(null);
  const [acting, setActing] = useState(false);
  const Icon = section.icon;

  useEffect(() => {
    api
      .get(section.getter)
      .then((r) => setCfg(r.data))
      .catch(() => setCfg({}));
  }, [section.getter]);

  const accept = async () => {
    setActing(true);
    try {
      await api.post(`/onboarding/review/${section.key}`);
      toast.success(`${section.label} marked reviewed`);
      onChange?.();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || `Couldn't mark ${section.label} reviewed`,
      );
    } finally {
      setActing(false);
    }
  };

  const statusBadge = () => {
    if (status === "customised")
      return (
        <span className="text-sky-400 text-xs font-bold flex items-center gap-1">
          <SettingsIcon className="h-3.5 w-3.5" /> Customised
        </span>
      );
    if (status === "reviewed")
      return (
        <span className="text-emerald-400 text-xs font-bold flex items-center gap-1">
          <CheckCircle2 className="h-3.5 w-3.5" /> Reviewed
        </span>
      );
    return <span className="text-slate-500 text-xs font-bold">Needs review</span>;
  };

  const rows = cfg && section.summary(cfg);

  return (
    <div
      className="rounded-lg border border-slate-800 bg-slate-900/80 overflow-hidden"
      data-testid={`onboarding-ops-${section.key}`}
    >
      <div className="p-4 flex items-start gap-3">
        <div
          className={`h-10 w-10 rounded-md ${section.accent} flex items-center justify-center shrink-0`}
        >
          <Icon className="h-5 w-5 text-white" strokeWidth={2.4} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm text-slate-100">{section.label}</div>
          <div className="text-xs text-slate-400">{section.sub}</div>
          <div className="mt-1">{statusBadge()}</div>

          {rows?.length ? (
            <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-slate-300">
              {rows.map(([k, v]) => (
                <div key={k} className="flex justify-between gap-2 min-w-0">
                  <dt className="text-slate-500 truncate">{k}</dt>
                  <dd className="font-mono text-slate-200 shrink-0">{v}</dd>
                </div>
              ))}
            </dl>
          ) : cfg === null ? (
            <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading defaults…
            </div>
          ) : null}
        </div>
      </div>
      <div className="border-t border-slate-800 bg-slate-950/40 px-4 py-2 flex items-center justify-end gap-2">
        <Link
          to={`/settings${section.anchor ? `#${section.anchor}` : ""}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-sky-400 hover:underline inline-flex items-center gap-1 mr-auto"
          data-testid={`onboarding-ops-${section.key}-customise`}
        >
          Customise in Settings <ExternalLink className="h-3 w-3" />
        </Link>
        {status === "pending" ? (
          <Button
            size="sm"
            onClick={accept}
            disabled={acting}
            data-testid={`onboarding-ops-${section.key}-accept`}
            className="bg-emerald-600 hover:bg-emerald-500 text-white"
          >
            {acting ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                Saving…
              </>
            ) : (
              <>Accept defaults</>
            )}
          </Button>
        ) : (
          <Button
            size="sm"
            variant="outline"
            onClick={accept}
            disabled={acting}
            data-testid={`onboarding-ops-${section.key}-reaccept`}
            className="text-slate-300"
          >
            Re-accept
          </Button>
        )}
      </div>
    </div>
  );
}
